/* eslint-disable */
/** Internal type. DO NOT USE DIRECTLY. */
type Exact<T extends { [key: string]: unknown }> = { [K in keyof T]: T[K] };
/** Internal type. DO NOT USE DIRECTLY. */
export type Incremental<T> = T | { [P in keyof T]?: P extends ' $fragmentName' | '__typename' ? T[P] : never };
import type { TypedDocumentNode as DocumentNode } from '@graphql-typed-document-node/core';
/**
 * Required: true
 *
 * Description: Input to record consent to a crypto terms bundle
 */
export type AcceptCryptoLegalDocInput = {
  /** Description: Company ID for legal entities. When provided, consent is recorded for the company. */
  companyId?: string | null | undefined;
  /** Description: Identifier of the terms bundle being accepted (from getActualCryptoLegalDocs) */
  cryptoProviderTermsId: number;
  /** Description: Where the consent was captured. Defaults to login_popup. */
  source?: CryptoConsentSource | null | undefined;
};

/** Description: Input data of auth */
export type AuthInput = {
  /**
   * Required: true
   *
   * Description: Captcha data
   *
   * Format:
   *
   *         Object
   */
  captcha?: ReCaptchaInput | null | undefined;
  /**
   * Required: true
   *
   * Description: User device
   *
   * Format:
   *
   *         enum
   */
  device: Device;
  hCaptchaToken?: string | null | undefined;
  /**
   * Required: true
   *
   * Description: User identity
   *
   * Format:
   *
   *         string
   */
  identity: string;
  /**
   * Required: false
   *
   * Description: User password
   *
   * Format:
   *
   *         string
   */
  password?: string | null | undefined;
  /**
   * Required: true
   *
   * Description: Service where the user is registered
   *
   * Format:
   *
   *         enum
   */
  service: Service;
  /**
   * Required: false
   *
   * Description: SRP-6 data
   *
   * Format:
   *
   *         Object
   */
  srp?: SrpVerifyInput | null | undefined;
};

/** Description: Input data of phone registration */
export type AuthRegisterPhoneInput = {
  /**
   * Required: true
   *
   * Description: Captcha data
   *
   * Format:
   *
   *         Object
   */
  captcha?: ReCaptchaInput | null | undefined;
  hCaptchaToken?: string | null | undefined;
  /**
   * Required: true
   *
   * Description: Identity
   *
   * Format:
   *
   *         string
   */
  identity: string;
};

/** Description: Input data of 2FA sending */
export type AuthTfaSend = {
  /**
   * Required: true
   *
   * Description: Operation id in Banxe system
   *
   * Format:
   *
   *         uuidv4
   */
  operationId: string;
  /**
   * Required: true
   *
   * Description: 2FA type
   *
   * Format:
   *
   *         enum
   */
  type: TfaType;
};

export type AuthUserKybDocumentStatus =
  | 'PENDING'
  | 'WAITING_FOR_UPLOAD_DOCUMENT';

/** Description: Input data for auth 2FA verification */
export type AuthVerify2FaInput = {
  /**
   * Required: false
   *
   * Description: Token for PUSH notification
   *
   * Format:
   *
   *         string
   */
  pushToken?: string | null | undefined;
  /**
   * Required: true
   *
   * Description: 2FA verification data
   *
   * Format:
   *
   *         Object
   */
  tfaVerifyData: Verify2FaInput;
};

export type BlockName =
  | 'CRYPTO_WITHDRAWAL_BLOCK'
  | 'FIAT_WITHDRAWAL_BLOCK'
  | 'FULL_CRYPTO_BLOCK'
  | 'FULL_FIAT_BLOCK'
  | 'FULL_USER_BLOCK'
  | 'SEPARATED_CRYPTO_BLOCK'
  | 'SEPARATED_FIAT_BLOCK';

export type BlockingType =
  | 'HARD'
  | 'SOFT'
  | 'WITHDRAW';

/** Description: Input data of device confirmation */
export type ConfirmInput = {
  /**
   * Required: true
   *
   * Description: Code value
   *
   * Format:
   *
   *         string
   */
  code: string;
  /**
   * Required: true
   *
   * Description: Device
   *
   * Format:
   *
   *         enum
   */
  device: Device;
  /**
   * Required: true
   *
   * Description: Identity
   *
   * Format:
   *
   *         string
   */
  identity: string;
  /**
   * Required: true
   *
   * Description: Code type
   *
   * Format:
   *
   *         enum
   */
  type: SendCode;
};

/** Description: Input data of password recovery confirmation */
export type ConfirmPasswordRecoveryInput = {
  /**
   * Required: true
   *
   * Description: Password recovery data
   *
   * Format:
   *
   *         Object
   */
  passwordRecovery: PasswordRecoveryInput;
  /**
   * Required: true
   *
   * Description: 2FA data
   *
   * Format:
   *
   *         Object
   */
  verify2FAInput: Verify2FaInput;
};

export type CryptoConsentSource =
  | 'LOGIN_POPUP'
  | 'SIGN_UP';

export type CryptoDocIssuer =
  | 'BANXE'
  | 'PAYBIS';

export type Device =
  | 'EMAIL'
  | 'EOS'
  | 'P256'
  | 'PHONE';

/**
 * Required: true
 *
 * Description: Input data to provide user full name
 */
export type FullNameInput = {
  /**
   * Required: true
   *
   * Description: User first name
   */
  firstName?: string | null | undefined;
  /**
   * Required: true
   *
   * Description: User last name
   */
  lastName?: string | null | undefined;
  /**
   * Required: true
   *
   * Description: User legal name
   */
  legalName?: string | null | undefined;
  /**
   * Required: true
   *
   * Description: User middle name
   */
  middleName?: string | null | undefined;
};

export type Gender =
  | 'FEMALE'
  | 'MALE'
  | 'OTHER';

export type GenderEnum =
  | 'FEMALE'
  | 'MALE'
  | 'OTHER';

export type InputReasonForAccountOpening =
  | 'DAILY_EXPENSES'
  | 'FAMILY_SUPPORT'
  | 'INVESTMENTS'
  | 'INVOICE_SETTLEMENTS'
  | 'OTHER'
  | 'PAYMENTS_FOR_SERVICES_AND_GOODS'
  | 'SALARY_DEPOSITS';

export type KeyEnum =
  | 'EOS'
  | 'P256';

/** Description: Input data of OAuth2 */
export type OAuth2Input = {
  /**
   * Required: true
   *
   * Description: Client id
   *
   * Format:
   *
   *         string
   */
  clientId: string;
  /**
   * Required: false
   *
   * Description: Login hint
   *
   * Format:
   *
   *         string
   */
  loginHint?: string | null | undefined;
  /**
   * Required: true
   *
   * Description: Redirect URI. Determines where the response is sent
   *
   * Format:
   *
   *         string
   */
  redirectUri: string;
  /**
   * Required: true
   *
   * Description: Response type
   *
   * Format:
   *
   *         enum
   */
  responseType: ResponseType;
  /**
   * Required: true
   *
   * Description: Scope
   *
   * Format:
   *
   *         string
   */
  scope: string;
  /**
   * Required: true
   *
   * Description: Service where the user is registered
   *
   * Format:
   *
   *         enum
   */
  service: Service;
  /**
   * Required: false
   *
   * Description: State
   *
   * Format:
   *
   *         string
   */
  state?: string | null | undefined;
};

/** Description: Input data of password recovery */
export type PasswordRecoveryInput = {
  hCaptchaToken?: string | null | undefined;
  /**
   * Required: false
   *
   * Description: New password
   *
   * Format:
   *
   *         string (8<= n <=64; with at least one capital character, one lower character, one number and with any special characters)
   */
  newPassword?: string | null | undefined;
  /**
   * Required: false
   *
   * Description: SRP-6 data
   *
   * Format:
   *
   *         Object
   */
  srp?: SrpInfo | null | undefined;
};

export type PlannedAccountUse =
  | 'BANK_TRANSFERS'
  | 'CARD_PAYMENTS'
  | 'COLLECTION_OF_FUNDS'
  | 'INTERNAL_PAYMENTS'
  | 'ONLINE_PURCHASES'
  | 'OTHER';

/**
 * Required: true
 *
 * Description: Input data to provide planned account use
 */
export type PlannedAccountUseInput = {
  /**
   * Required: false
   *
   * Description: Other planned account use. Use if list of reasons have "OTHER" reason type
   */
  other?: string | null | undefined;
  /**
   * Required: true
   *
   * Description: Planned account use
   */
  uses?: Array<PlannedAccountUse> | null | undefined;
};

export type ReCaptchaInput = {
  captchaAction?: string | null | undefined;
  captchaToken: string;
  useCaptchaV2?: boolean | null | undefined;
};

/**
 * Required: true
 *
 * Description: Input data to provide reasons for account opening
 */
export type ReasonForAccountOpeningInput = {
  /**
   * Required: false
   *
   * Description: Other reasons for account opening. Use if list of reasons have "OTHER" reason type
   */
  other?: string | null | undefined;
  /**
   * Required: true
   *
   * Description: Reasons for account opening
   */
  reasons?: Array<InputReasonForAccountOpening> | null | undefined;
};

export type ResponseType =
  | 'CODE';

export type RoleEnum =
  | 'ADMIN'
  | 'AML_OFFICER'
  | 'MODERATOR'
  | 'SERVICE'
  | 'SUPER'
  | 'USER';

export type SendCode =
  | 'CHANGE_EMAIL'
  | 'CHANGE_PHONE'
  | 'FORGOTPASSWORD'
  | 'REGISTRATION'
  | 'REGISTRATION_PHONE';

/** Input data of sending code on device */
export type SendCodeInput = {
  /**
   * Required: true
   *
   * Description: Captcha data
   *
   * Format:
   *
   *         Object
   */
  captcha?: ReCaptchaInput | null | undefined;
  /**
   * Required: true
   *
   * Description: Device
   *
   * Format:
   *
   *         enum
   */
  device: Device;
  hCaptchaToken?: string | null | undefined;
  /**
   * Required: true
   *
   * Description: User identity (email/phone)
   *
   * Format:
   *
   *         string (email/phone format)
   */
  identity: string;
  /**
   * Required: true
   *
   * Description: Service where the user is registered
   *
   * Format:
   *
   *         enum
   */
  service: Service;
  /**
   * Required: true
   *
   * Description: Code type
   *
   * Format:
   *
   *         enum
   */
  type?: SendCode | null | undefined;
  /**
   * Required: false
   *
   * Description: User id in Banxe system
   *
   * Format:
   *
   *         uuidv4
   */
  userId?: string | null | undefined;
};

export type Service =
  | 'BANXEID'
  | 'CARDS'
  | 'CEX'
  | 'CRYPTOAPI'
  | 'CRYPTO_PROCESSING'
  | 'DCARD'
  | 'DEFI'
  | 'DEFI_INVEST'
  | 'FAST_EXCHANGE'
  | 'HERMESUS'
  | 'NEURONEX'
  | 'NEURONWALLET'
  | 'P2P'
  | 'TOMPAY'
  | 'TRANSACTION_SERVICE';

/** Input data of SRP in sign up */
export type SrpInfo = {
  /**
   * Required: true
   *
   * Description: SRP-6 salt
   *
   * Format:
   *
   *         string
   */
  salt: string;
  /**
   * Required: true
   *
   * Description: SRP-6 verifier
   *
   * Format:
   *
   *         string
   */
  verifier: string;
};

/** Description: Input data of SRP-6 auth */
export type SrpVerifyInput = {
  /**
   * Required: true
   *
   * Description: SRP-6 public ephemerial
   *
   * Format:
   *
   *         string
   */
  clientPublicEphemeral: string;
  /**
   * Required: true
   *
   * Description: Operaion id
   *
   * Format:
   *
   *         string
   */
  operationId: string;
  /**
   * Required: true
   *
   * Description: SRP-6 proof
   *
   * Format:
   *
   *         string
   */
  proof: string;
};

export type StatusEnum =
  | 'ACTIVE'
  | 'BLOCKED'
  | 'UNCONFIRMED';

export type TfaType =
  | 'EMAIL'
  | 'OTP'
  | 'SMS';

/** Description: Input data of updating extra */
export type UpdateExtraInput = {
  /**
   * Required: true
   *
   * Description: Value
   *
   * Format:
   *
   *         JSON
   */
  data: unknown;
  /**
   * Required: true
   *
   * Description: Key
   *
   * Format:
   *
   *         string
   */
  key: string;
};

/**
 * Required: true
 *
 * Description: Input data to update my personal info
 */
export type UpdateMyPersonalInput = {
  /**
   * Required: false
   *
   * Description: User birth date
   */
  birthDate?: string | null | undefined;
  /**
   * Required: false
   *
   * Description: User gender
   */
  gender?: Gender | null | undefined;
  isFamilyMemberPep?: boolean | null | undefined;
  isGdprAccepted?: boolean | null | undefined;
  isPep?: boolean | null | undefined;
  isUsa?: boolean | null | undefined;
  /**
   * Required: false
   *
   * Description: User name
   */
  name?: FullNameInput | null | undefined;
  /**
   * Required: false
   *
   * Description: Nationality
   */
  nationality?: string | null | undefined;
  /**
   * Required: false
   *
   * Description: Nationality country identifier
   */
  nationalityCountryId?: string | number | null | undefined;
  /**
   * Required: false
   *
   * Description: Address identifier where user was born
   */
  placeOfBirthAddressId?: string | number | null | undefined;
  /**
   * Required: false
   *
   * Description: Country identifier where user was born
   */
  placeOfBirthCountryId?: string | number | null | undefined;
  plannedAccountUse?: PlannedAccountUseInput | null | undefined;
  reasonForAccountOpening?: ReasonForAccountOpeningInput | null | undefined;
  /**
   * Required: false
   *
   * Description: Residence country identifier
   */
  residenceCountryId?: string | number | null | undefined;
  termsAndConditions?: boolean | null | undefined;
  tin?: string | null | undefined;
};

/** Description: Input data for sign up */
export type UserInput = {
  /**
   * Required: false
   *
   * Description: Avatar (file) id in Banxe system
   *
   * Format:
   *
   *         uuidv4
   */
  avatarId?: string | null | undefined;
  /**
   * Required: false
   *
   * Description: Birth day
   *
   * Format:
   *
   *         Date
   */
  birthDate?: string | null | undefined;
  /**
   * Required: true
   *
   * Description: Captcha data
   *
   * Format:
   *
   *         Object
   */
  captcha?: ReCaptchaInput | null | undefined;
  /**
   * Required: true
   *
   * Description: 2FA code
   *
   * Format:
   *
   *         string (6 characters)
   */
  code: string;
  /**
   * Required: true
   *
   * Description: Device
   *
   * Format:
   *
   *         enum
   */
  device: Device;
  /**
   * Required: false
   *
   * Description: Uder extra data
   *
   * Format:
   *
   *         JSON
   */
  extra?: unknown;
  /**
   * Required: false
   *
   * Description: First name
   *
   * Format:
   *
   *         string
   */
  firstName?: string | null | undefined;
  /**
   * Required: false
   *
   * Description: Gender
   *
   * Format:
   *
   *         enum
   */
  gender?: GenderEnum | null | undefined;
  hCaptchaToken?: string | null | undefined;
  /**
   * Required: true
   *
   * Description: User identity (+70000000000/email@mail.com)
   *
   * Format:
   *
   *         string (email/phone format)
   */
  identity: string;
  /**
   * Required: false
   *
   * Description: GDPR
   *
   * Format:
   *
   *         boolean
   */
  isGdprAccepted?: boolean | null | undefined;
  /**
   * Required: false
   *
   * Description: Last name
   *
   * Format:
   *
   *         string
   */
  lastName?: string | null | undefined;
  /**
   * Required: false
   *
   * Description: Password
   *
   * Format:
   *
   *         string (8<= n <=64; with at least one capital character, one lower character, one number and with any special characters)
   */
  password?: string | null | undefined;
  /**
   * Required: false
   *
   * Description: User real email
   *
   * Format:
   *
   *         string
   */
  realEmail?: string | null | undefined;
  /**
   * Required: false
   *
   * Description: Referral program participant code
   *
   * Format:
   *
   *         string
   */
  referralCode?: string | null | undefined;
  /**
   * Required: false
   *
   * Description: Region client (e.g. DE, US, CH)
   *
   * Format:
   *
   *         string
   */
  region?: string | null | undefined;
  /**
   * Required: true
   *
   * Description: Service where the user is registered
   *
   * Format:
   *
   *         enum
   */
  service: Service;
  /** Acquisition source / referral / UTM channel */
  source?: string | null | undefined;
  /**
   * Required: false
   *
   * Description: SRP-6 data
   *
   * Format:
   *
   *         Object
   */
  srp?: SrpInfo | null | undefined;
  /**
   * Required: false
   *
   * Description: Username
   *
   * Format:
   *
   *         string
   */
  username?: string | null | undefined;
  /**
   * Required: false
   *
   * Description: Metabase data
   *
   * Format:
   *
   *         Object
   */
  utm?: UtmInput | null | undefined;
};

export type UserTariff =
  | 'BANXE_PLUS';

/** Description: Input data for metabase */
export type UtmInput = {
  /**
   * Required: false
   *
   * Description: metabase campaign
   *
   * Format:
   *
   *         string
   */
  utm_campaign?: string | null | undefined;
  /**
   * Required: false
   *
   * Description: metabase content
   *
   * Format:
   *
   *         string
   */
  utm_content?: string | null | undefined;
  /**
   * Required: false
   *
   * Description: metabase medium
   *
   * Format:
   *
   *         string
   */
  utm_medium?: string | null | undefined;
  /**
   * Required: false
   *
   * Description: metabase source
   *
   * Format:
   *
   *         string
   */
  utm_source?: string | null | undefined;
  /**
   * Required: false
   *
   * Description: metabase term
   *
   * Format:
   *
   *         string
   */
  utm_term?: string | null | undefined;
};

export type Verify2FaInput = {
  operationId: string;
  tokens: Array<VerifyTokenInput>;
};

export type VerifyTokenInput = {
  token: string;
  type: TfaType;
};

export type AcceptCryptoLegalDocMutationVariables = Exact<{
  input: AcceptCryptoLegalDocInput;
}>;


export type AcceptCryptoLegalDocMutation = { acceptCryptoLegalDoc: { requiresConsent: boolean, hasAccepted: boolean, currentTermsId: number | null, currentTermsVersion: string | null } };

export type ConfirmPasswordRecoveryMutationVariables = Exact<{
  input: ConfirmPasswordRecoveryInput;
}>;


export type ConfirmPasswordRecoveryMutation = { confirmPasswordRecovery: boolean };

export type ConfirmSignInMutationVariables = Exact<{
  input: AuthVerify2FaInput;
}>;


export type ConfirmSignInMutation = { confirmSignIn: { accessToken: string, expiresIn: number, idToken: string | null, refreshToken: string | null, tokenType: string } };

export type CreateAccountMutationVariables = Exact<{
  input: UserInput;
}>;


export type CreateAccountMutation = { signUp: { __typename: 'RegisteredUser', user: { __typename: 'User', id: string }, auth: { __typename: 'Auth', accessToken: string, refreshToken: string | null } } };

export type InitUploadSessionMutationVariables = Exact<{ [key: string]: never; }>;


export type InitUploadSessionMutation = { initUploadSession: { url: string } };

export type MyUpdateExtraMutationVariables = Exact<{
  input: UpdateExtraInput;
}>;


export type MyUpdateExtraMutation = { myUpdateExtra: string };

export type PaybisEnsureKybStatusMutationVariables = Exact<{
  companyId: string;
  companyName?: string | null | undefined;
  email?: string | null | undefined;
}>;


export type PaybisEnsureKybStatusMutation = { paybisEnsureKybStatus: { companyId: string, createdAt: string, id: string, notes: string | null, status: string, updatedAt: string, updatedBy: string | null } };

export type PaybisUpdateKybTcStatusMutationVariables = Exact<{
  companyId: string;
  tcStatus: string;
}>;


export type PaybisUpdateKybTcStatusMutation = { paybisUpdateKybTcStatus: { companyId: string, companyName: string | null, createdAt: string, email: string | null, id: string, notes: string | null, status: string, tcStatus: string | null, updatedAt: string, updatedBy: string | null, updatedByName: string | null } };

export type RegisterPhoneMutationVariables = Exact<{
  input: AuthRegisterPhoneInput;
}>;


export type RegisterPhoneMutation = { registerPhone: { __typename: 'CheckResendOneTimeCodeType', device: Device, deviceId: string, emailRetryAfter: string | null, emailTimeout: number | null, smsRetryAfter: string | null, smsTimeout: number | null } };

export type ResendTwoFaCodeMutationVariables = Exact<{
  input: AuthTfaSend;
}>;


export type ResendTwoFaCodeMutation = { sendTFACode: { __typename: 'Send2FATokenResponse', operationId: string, smsTimeout: number | null, emailTimeout: number | null } };

export type SignUpMutationVariables = Exact<{
  input: UserInput;
}>;


export type SignUpMutation = { signUp: { user: { id: string }, auth: { accessToken: string, refreshToken: string | null, expiresIn: number } } };

export type StartPasswordRecoveryMutationVariables = Exact<{
  identity: string;
}>;


export type StartPasswordRecoveryMutation = { startPasswordRecovery: { __typename: 'Send2FATokenResponse', operationId: string, response: Array<TfaType>, smsTimeout: number | null, emailTimeout: number | null } };

export type UpdateMyPersonalMutationVariables = Exact<{
  input: UpdateMyPersonalInput;
}>;


export type UpdateMyPersonalMutation = { updateMyPersonal: { __typename: 'UserPersonal', tin: string | null } };

export type CheckReferrersQueryVariables = Exact<{
  inviteCode: string;
}>;


export type CheckReferrersQuery = { checkReferrerCode: boolean };

export type ConfirmQueryVariables = Exact<{
  input: ConfirmInput;
}>;


export type ConfirmQuery = { confirm: boolean };

export type GetActualCryptoLegalDocsQueryVariables = Exact<{ [key: string]: never; }>;


export type GetActualCryptoLegalDocsQuery = { getActualCryptoLegalDocs: { id: number, version: string, providerName: string, documents: Array<{ id: number, issuer: CryptoDocIssuer, name: string, version: string | null, url: string | null }> } | null };

export type OAuthQueryVariables = Exact<{
  input: OAuth2Input;
}>;


export type OAuthQuery = { oauth2: { __typename: 'Redirect', url: string, statusCode: number } };

export type MyCryptoConsentStatusQueryVariables = Exact<{
  companyId?: string | null | undefined;
}>;


export type MyCryptoConsentStatusQuery = { myCryptoConsentStatus: { requiresConsent: boolean, hasAccepted: boolean, currentTermsId: number | null, currentTermsVersion: string | null } };

export type MyGetExtraQueryVariables = Exact<{
  key: string;
}>;


export type MyGetExtraQuery = { myGetExtra: unknown };

export type PaybisKybStatusQueryVariables = Exact<{
  companyId: string;
}>;


export type PaybisKybStatusQuery = { paybisKybStatus: { companyId: string, companyName: string | null, createdAt: string, email: string | null, id: string, notes: string | null, status: string, tcStatus: string | null, updatedAt: string, updatedBy: string | null, updatedByName: string | null } | null };

export type SendCodeQueryVariables = Exact<{
  input: SendCodeInput;
}>;


export type SendCodeQuery = { sendCode: { device: Device, deviceId: string, smsTimeout: number | null, emailTimeout: number | null, smsRetryAfter: string | null, emailRetryAfter: string | null } | null };

export type SignInQueryVariables = Exact<{
  input: AuthInput;
}>;


export type SignInQuery = { signIn: { emailRetryAfter: string | null, emailTimeout: number | null, operationId: string, phoneNumber: string | null, response: Array<TfaType>, smsRetryAfter: string | null, smsTimeout: number | null, srpServerProof: string | null } };

export type ViewerQueryVariables = Exact<{ [key: string]: never; }>;


export type ViewerQuery = { viewer: { appleUserId: string | null, birthDate: string | null, chatClientId: string | null, chatClientIdentity: string | null, createdAt: string, externalUserId: string | null, extra: unknown, firstName: string | null, gender: GenderEnum | null, kybDocumentStatus: AuthUserKybDocumentStatus, language: string, lastLoginDate: string | null, lastName: string | null, leadId: string | null, partnerId: string | null, role: RoleEnum, service: Service, status: StatusEnum, sub: string | null, tariff: UserTariff, timezone: string, updatedAt: string, userId: string, username: string | null, EOS: { key: string, type: KeyEnum } | null, avatar: { url: string, id: string } | null, blockingStatusInfo: Array<{ accountIds: Array<string> | null, blockName: BlockName, isBlocked: boolean, reasons: Array<string> | null, types: Array<BlockingType> | null }> | null, companies: Array<{ legalName: string | null, id: string }> | null, email: { confirm: boolean, deviceId: string, identity: string, type: Device } | null, p256: { key: string, type: KeyEnum } | null, phone: { confirm: boolean, deviceId: string, identity: string, type: Device } | null, referrerInfo: { id: string, inviteCode: string, referrals: Array<string | null> } | null } };


export const AcceptCryptoLegalDocDocument = {"kind":"Document","definitions":[{"kind":"OperationDefinition","operation":"mutation","name":{"kind":"Name","value":"AcceptCryptoLegalDoc"},"variableDefinitions":[{"kind":"VariableDefinition","variable":{"kind":"Variable","name":{"kind":"Name","value":"input"}},"type":{"kind":"NonNullType","type":{"kind":"NamedType","name":{"kind":"Name","value":"AcceptCryptoLegalDocInput"}}}}],"selectionSet":{"kind":"SelectionSet","selections":[{"kind":"Field","name":{"kind":"Name","value":"acceptCryptoLegalDoc"},"arguments":[{"kind":"Argument","name":{"kind":"Name","value":"input"},"value":{"kind":"Variable","name":{"kind":"Name","value":"input"}}}],"selectionSet":{"kind":"SelectionSet","selections":[{"kind":"Field","name":{"kind":"Name","value":"requiresConsent"}},{"kind":"Field","name":{"kind":"Name","value":"hasAccepted"}},{"kind":"Field","name":{"kind":"Name","value":"currentTermsId"}},{"kind":"Field","name":{"kind":"Name","value":"currentTermsVersion"}}]}}]}}]} as unknown as DocumentNode<AcceptCryptoLegalDocMutation, AcceptCryptoLegalDocMutationVariables>;
export const ConfirmPasswordRecoveryDocument = {"kind":"Document","definitions":[{"kind":"OperationDefinition","operation":"mutation","name":{"kind":"Name","value":"ConfirmPasswordRecovery"},"variableDefinitions":[{"kind":"VariableDefinition","variable":{"kind":"Variable","name":{"kind":"Name","value":"input"}},"type":{"kind":"NonNullType","type":{"kind":"NamedType","name":{"kind":"Name","value":"ConfirmPasswordRecoveryInput"}}}}],"selectionSet":{"kind":"SelectionSet","selections":[{"kind":"Field","name":{"kind":"Name","value":"confirmPasswordRecovery"},"arguments":[{"kind":"Argument","name":{"kind":"Name","value":"input"},"value":{"kind":"Variable","name":{"kind":"Name","value":"input"}}}]}]}}]} as unknown as DocumentNode<ConfirmPasswordRecoveryMutation, ConfirmPasswordRecoveryMutationVariables>;
export const ConfirmSignInDocument = {"kind":"Document","definitions":[{"kind":"OperationDefinition","operation":"mutation","name":{"kind":"Name","value":"ConfirmSignIn"},"variableDefinitions":[{"kind":"VariableDefinition","variable":{"kind":"Variable","name":{"kind":"Name","value":"input"}},"type":{"kind":"NonNullType","type":{"kind":"NamedType","name":{"kind":"Name","value":"AuthVerify2FAInput"}}}}],"selectionSet":{"kind":"SelectionSet","selections":[{"kind":"Field","name":{"kind":"Name","value":"confirmSignIn"},"arguments":[{"kind":"Argument","name":{"kind":"Name","value":"input"},"value":{"kind":"Variable","name":{"kind":"Name","value":"input"}}}],"selectionSet":{"kind":"SelectionSet","selections":[{"kind":"Field","name":{"kind":"Name","value":"accessToken"}},{"kind":"Field","name":{"kind":"Name","value":"expiresIn"}},{"kind":"Field","name":{"kind":"Name","value":"idToken"}},{"kind":"Field","name":{"kind":"Name","value":"refreshToken"}},{"kind":"Field","name":{"kind":"Name","value":"tokenType"}}]}}]}}]} as unknown as DocumentNode<ConfirmSignInMutation, ConfirmSignInMutationVariables>;
export const CreateAccountDocument = {"kind":"Document","definitions":[{"kind":"OperationDefinition","operation":"mutation","name":{"kind":"Name","value":"CreateAccount"},"variableDefinitions":[{"kind":"VariableDefinition","variable":{"kind":"Variable","name":{"kind":"Name","value":"input"}},"type":{"kind":"NonNullType","type":{"kind":"NamedType","name":{"kind":"Name","value":"UserInput"}}}}],"selectionSet":{"kind":"SelectionSet","selections":[{"kind":"Field","name":{"kind":"Name","value":"signUp"},"arguments":[{"kind":"Argument","name":{"kind":"Name","value":"input"},"value":{"kind":"Variable","name":{"kind":"Name","value":"input"}}}],"selectionSet":{"kind":"SelectionSet","selections":[{"kind":"Field","name":{"kind":"Name","value":"user"},"selectionSet":{"kind":"SelectionSet","selections":[{"kind":"Field","name":{"kind":"Name","value":"id"}},{"kind":"Field","name":{"kind":"Name","value":"__typename"}}]}},{"kind":"Field","name":{"kind":"Name","value":"auth"},"selectionSet":{"kind":"SelectionSet","selections":[{"kind":"Field","name":{"kind":"Name","value":"accessToken"}},{"kind":"Field","name":{"kind":"Name","value":"refreshToken"}},{"kind":"Field","name":{"kind":"Name","value":"__typename"}}]}},{"kind":"Field","name":{"kind":"Name","value":"__typename"}}]}}]}}]} as unknown as DocumentNode<CreateAccountMutation, CreateAccountMutationVariables>;
export const InitUploadSessionDocument = {"kind":"Document","definitions":[{"kind":"OperationDefinition","operation":"mutation","name":{"kind":"Name","value":"InitUploadSession"},"selectionSet":{"kind":"SelectionSet","selections":[{"kind":"Field","name":{"kind":"Name","value":"initUploadSession"},"selectionSet":{"kind":"SelectionSet","selections":[{"kind":"Field","name":{"kind":"Name","value":"url"}}]}}]}}]} as unknown as DocumentNode<InitUploadSessionMutation, InitUploadSessionMutationVariables>;
export const MyUpdateExtraDocument = {"kind":"Document","definitions":[{"kind":"OperationDefinition","operation":"mutation","name":{"kind":"Name","value":"MyUpdateExtra"},"variableDefinitions":[{"kind":"VariableDefinition","variable":{"kind":"Variable","name":{"kind":"Name","value":"input"}},"type":{"kind":"NonNullType","type":{"kind":"NamedType","name":{"kind":"Name","value":"UpdateExtraInput"}}}}],"selectionSet":{"kind":"SelectionSet","selections":[{"kind":"Field","name":{"kind":"Name","value":"myUpdateExtra"},"arguments":[{"kind":"Argument","name":{"kind":"Name","value":"input"},"value":{"kind":"Variable","name":{"kind":"Name","value":"input"}}}]}]}}]} as unknown as DocumentNode<MyUpdateExtraMutation, MyUpdateExtraMutationVariables>;
export const PaybisEnsureKybStatusDocument = {"kind":"Document","definitions":[{"kind":"OperationDefinition","operation":"mutation","name":{"kind":"Name","value":"PaybisEnsureKybStatus"},"variableDefinitions":[{"kind":"VariableDefinition","variable":{"kind":"Variable","name":{"kind":"Name","value":"companyId"}},"type":{"kind":"NonNullType","type":{"kind":"NamedType","name":{"kind":"Name","value":"String"}}}},{"kind":"VariableDefinition","variable":{"kind":"Variable","name":{"kind":"Name","value":"companyName"}},"type":{"kind":"NamedType","name":{"kind":"Name","value":"String"}}},{"kind":"VariableDefinition","variable":{"kind":"Variable","name":{"kind":"Name","value":"email"}},"type":{"kind":"NamedType","name":{"kind":"Name","value":"String"}}}],"selectionSet":{"kind":"SelectionSet","selections":[{"kind":"Field","name":{"kind":"Name","value":"paybisEnsureKybStatus"},"arguments":[{"kind":"Argument","name":{"kind":"Name","value":"companyId"},"value":{"kind":"Variable","name":{"kind":"Name","value":"companyId"}}},{"kind":"Argument","name":{"kind":"Name","value":"companyName"},"value":{"kind":"Variable","name":{"kind":"Name","value":"companyName"}}},{"kind":"Argument","name":{"kind":"Name","value":"email"},"value":{"kind":"Variable","name":{"kind":"Name","value":"email"}}}],"selectionSet":{"kind":"SelectionSet","selections":[{"kind":"Field","name":{"kind":"Name","value":"companyId"}},{"kind":"Field","name":{"kind":"Name","value":"createdAt"}},{"kind":"Field","name":{"kind":"Name","value":"id"}},{"kind":"Field","name":{"kind":"Name","value":"notes"}},{"kind":"Field","name":{"kind":"Name","value":"status"}},{"kind":"Field","name":{"kind":"Name","value":"updatedAt"}},{"kind":"Field","name":{"kind":"Name","value":"updatedBy"}}]}}]}}]} as unknown as DocumentNode<PaybisEnsureKybStatusMutation, PaybisEnsureKybStatusMutationVariables>;
export const PaybisUpdateKybTcStatusDocument = {"kind":"Document","definitions":[{"kind":"OperationDefinition","operation":"mutation","name":{"kind":"Name","value":"PaybisUpdateKybTcStatus"},"variableDefinitions":[{"kind":"VariableDefinition","variable":{"kind":"Variable","name":{"kind":"Name","value":"companyId"}},"type":{"kind":"NonNullType","type":{"kind":"NamedType","name":{"kind":"Name","value":"String"}}}},{"kind":"VariableDefinition","variable":{"kind":"Variable","name":{"kind":"Name","value":"tcStatus"}},"type":{"kind":"NonNullType","type":{"kind":"NamedType","name":{"kind":"Name","value":"String"}}}}],"selectionSet":{"kind":"SelectionSet","selections":[{"kind":"Field","name":{"kind":"Name","value":"paybisUpdateKybTcStatus"},"arguments":[{"kind":"Argument","name":{"kind":"Name","value":"companyId"},"value":{"kind":"Variable","name":{"kind":"Name","value":"companyId"}}},{"kind":"Argument","name":{"kind":"Name","value":"tcStatus"},"value":{"kind":"Variable","name":{"kind":"Name","value":"tcStatus"}}}],"selectionSet":{"kind":"SelectionSet","selections":[{"kind":"Field","name":{"kind":"Name","value":"companyId"}},{"kind":"Field","name":{"kind":"Name","value":"companyName"}},{"kind":"Field","name":{"kind":"Name","value":"createdAt"}},{"kind":"Field","name":{"kind":"Name","value":"email"}},{"kind":"Field","name":{"kind":"Name","value":"id"}},{"kind":"Field","name":{"kind":"Name","value":"notes"}},{"kind":"Field","name":{"kind":"Name","value":"status"}},{"kind":"Field","name":{"kind":"Name","value":"tcStatus"}},{"kind":"Field","name":{"kind":"Name","value":"updatedAt"}},{"kind":"Field","name":{"kind":"Name","value":"updatedBy"}},{"kind":"Field","name":{"kind":"Name","value":"updatedByName"}}]}}]}}]} as unknown as DocumentNode<PaybisUpdateKybTcStatusMutation, PaybisUpdateKybTcStatusMutationVariables>;
export const RegisterPhoneDocument = {"kind":"Document","definitions":[{"kind":"OperationDefinition","operation":"mutation","name":{"kind":"Name","value":"RegisterPhone"},"variableDefinitions":[{"kind":"VariableDefinition","variable":{"kind":"Variable","name":{"kind":"Name","value":"input"}},"type":{"kind":"NonNullType","type":{"kind":"NamedType","name":{"kind":"Name","value":"AuthRegisterPhoneInput"}}}}],"selectionSet":{"kind":"SelectionSet","selections":[{"kind":"Field","name":{"kind":"Name","value":"registerPhone"},"arguments":[{"kind":"Argument","name":{"kind":"Name","value":"input"},"value":{"kind":"Variable","name":{"kind":"Name","value":"input"}}}],"selectionSet":{"kind":"SelectionSet","selections":[{"kind":"Field","name":{"kind":"Name","value":"device"}},{"kind":"Field","name":{"kind":"Name","value":"deviceId"}},{"kind":"Field","name":{"kind":"Name","value":"emailRetryAfter"}},{"kind":"Field","name":{"kind":"Name","value":"emailTimeout"}},{"kind":"Field","name":{"kind":"Name","value":"smsRetryAfter"}},{"kind":"Field","name":{"kind":"Name","value":"smsTimeout"}},{"kind":"Field","name":{"kind":"Name","value":"__typename"}}]}}]}}]} as unknown as DocumentNode<RegisterPhoneMutation, RegisterPhoneMutationVariables>;
export const ResendTwoFaCodeDocument = {"kind":"Document","definitions":[{"kind":"OperationDefinition","operation":"mutation","name":{"kind":"Name","value":"ResendTwoFACode"},"variableDefinitions":[{"kind":"VariableDefinition","variable":{"kind":"Variable","name":{"kind":"Name","value":"input"}},"type":{"kind":"NonNullType","type":{"kind":"NamedType","name":{"kind":"Name","value":"AuthTFASend"}}}}],"selectionSet":{"kind":"SelectionSet","selections":[{"kind":"Field","name":{"kind":"Name","value":"sendTFACode"},"arguments":[{"kind":"Argument","name":{"kind":"Name","value":"input"},"value":{"kind":"Variable","name":{"kind":"Name","value":"input"}}}],"selectionSet":{"kind":"SelectionSet","selections":[{"kind":"Field","name":{"kind":"Name","value":"operationId"}},{"kind":"Field","name":{"kind":"Name","value":"smsTimeout"}},{"kind":"Field","name":{"kind":"Name","value":"emailTimeout"}},{"kind":"Field","name":{"kind":"Name","value":"__typename"}}]}}]}}]} as unknown as DocumentNode<ResendTwoFaCodeMutation, ResendTwoFaCodeMutationVariables>;
export const SignUpDocument = {"kind":"Document","definitions":[{"kind":"OperationDefinition","operation":"mutation","name":{"kind":"Name","value":"SignUp"},"variableDefinitions":[{"kind":"VariableDefinition","variable":{"kind":"Variable","name":{"kind":"Name","value":"input"}},"type":{"kind":"NonNullType","type":{"kind":"NamedType","name":{"kind":"Name","value":"UserInput"}}}}],"selectionSet":{"kind":"SelectionSet","selections":[{"kind":"Field","name":{"kind":"Name","value":"signUp"},"arguments":[{"kind":"Argument","name":{"kind":"Name","value":"input"},"value":{"kind":"Variable","name":{"kind":"Name","value":"input"}}}],"selectionSet":{"kind":"SelectionSet","selections":[{"kind":"Field","name":{"kind":"Name","value":"user"},"selectionSet":{"kind":"SelectionSet","selections":[{"kind":"Field","name":{"kind":"Name","value":"id"}}]}},{"kind":"Field","name":{"kind":"Name","value":"auth"},"selectionSet":{"kind":"SelectionSet","selections":[{"kind":"Field","name":{"kind":"Name","value":"accessToken"}},{"kind":"Field","name":{"kind":"Name","value":"refreshToken"}},{"kind":"Field","name":{"kind":"Name","value":"expiresIn"}}]}}]}}]}}]} as unknown as DocumentNode<SignUpMutation, SignUpMutationVariables>;
export const StartPasswordRecoveryDocument = {"kind":"Document","definitions":[{"kind":"OperationDefinition","operation":"mutation","name":{"kind":"Name","value":"StartPasswordRecovery"},"variableDefinitions":[{"kind":"VariableDefinition","variable":{"kind":"Variable","name":{"kind":"Name","value":"identity"}},"type":{"kind":"NonNullType","type":{"kind":"NamedType","name":{"kind":"Name","value":"String"}}}}],"selectionSet":{"kind":"SelectionSet","selections":[{"kind":"Field","name":{"kind":"Name","value":"startPasswordRecovery"},"arguments":[{"kind":"Argument","name":{"kind":"Name","value":"identity"},"value":{"kind":"Variable","name":{"kind":"Name","value":"identity"}}}],"selectionSet":{"kind":"SelectionSet","selections":[{"kind":"Field","name":{"kind":"Name","value":"operationId"}},{"kind":"Field","name":{"kind":"Name","value":"response"}},{"kind":"Field","name":{"kind":"Name","value":"smsTimeout"}},{"kind":"Field","name":{"kind":"Name","value":"emailTimeout"}},{"kind":"Field","name":{"kind":"Name","value":"__typename"}}]}}]}}]} as unknown as DocumentNode<StartPasswordRecoveryMutation, StartPasswordRecoveryMutationVariables>;
export const UpdateMyPersonalDocument = {"kind":"Document","definitions":[{"kind":"OperationDefinition","operation":"mutation","name":{"kind":"Name","value":"UpdateMyPersonal"},"variableDefinitions":[{"kind":"VariableDefinition","variable":{"kind":"Variable","name":{"kind":"Name","value":"input"}},"type":{"kind":"NonNullType","type":{"kind":"NamedType","name":{"kind":"Name","value":"UpdateMyPersonalInput"}}}}],"selectionSet":{"kind":"SelectionSet","selections":[{"kind":"Field","name":{"kind":"Name","value":"updateMyPersonal"},"arguments":[{"kind":"Argument","name":{"kind":"Name","value":"input"},"value":{"kind":"Variable","name":{"kind":"Name","value":"input"}}}],"selectionSet":{"kind":"SelectionSet","selections":[{"kind":"Field","name":{"kind":"Name","value":"tin"}},{"kind":"Field","name":{"kind":"Name","value":"__typename"}}]}}]}}]} as unknown as DocumentNode<UpdateMyPersonalMutation, UpdateMyPersonalMutationVariables>;
export const CheckReferrersDocument = {"kind":"Document","definitions":[{"kind":"OperationDefinition","operation":"query","name":{"kind":"Name","value":"CheckReferrers"},"variableDefinitions":[{"kind":"VariableDefinition","variable":{"kind":"Variable","name":{"kind":"Name","value":"inviteCode"}},"type":{"kind":"NonNullType","type":{"kind":"NamedType","name":{"kind":"Name","value":"String"}}}}],"selectionSet":{"kind":"SelectionSet","selections":[{"kind":"Field","name":{"kind":"Name","value":"checkReferrerCode"},"arguments":[{"kind":"Argument","name":{"kind":"Name","value":"inviteCode"},"value":{"kind":"Variable","name":{"kind":"Name","value":"inviteCode"}}}]}]}}]} as unknown as DocumentNode<CheckReferrersQuery, CheckReferrersQueryVariables>;
export const ConfirmDocument = {"kind":"Document","definitions":[{"kind":"OperationDefinition","operation":"query","name":{"kind":"Name","value":"Confirm"},"variableDefinitions":[{"kind":"VariableDefinition","variable":{"kind":"Variable","name":{"kind":"Name","value":"input"}},"type":{"kind":"NonNullType","type":{"kind":"NamedType","name":{"kind":"Name","value":"ConfirmInput"}}}}],"selectionSet":{"kind":"SelectionSet","selections":[{"kind":"Field","name":{"kind":"Name","value":"confirm"},"arguments":[{"kind":"Argument","name":{"kind":"Name","value":"input"},"value":{"kind":"Variable","name":{"kind":"Name","value":"input"}}}]}]}}]} as unknown as DocumentNode<ConfirmQuery, ConfirmQueryVariables>;
export const GetActualCryptoLegalDocsDocument = {"kind":"Document","definitions":[{"kind":"OperationDefinition","operation":"query","name":{"kind":"Name","value":"GetActualCryptoLegalDocs"},"selectionSet":{"kind":"SelectionSet","selections":[{"kind":"Field","name":{"kind":"Name","value":"getActualCryptoLegalDocs"},"selectionSet":{"kind":"SelectionSet","selections":[{"kind":"Field","name":{"kind":"Name","value":"id"}},{"kind":"Field","name":{"kind":"Name","value":"version"}},{"kind":"Field","name":{"kind":"Name","value":"providerName"}},{"kind":"Field","name":{"kind":"Name","value":"documents"},"selectionSet":{"kind":"SelectionSet","selections":[{"kind":"Field","name":{"kind":"Name","value":"id"}},{"kind":"Field","name":{"kind":"Name","value":"issuer"}},{"kind":"Field","name":{"kind":"Name","value":"name"}},{"kind":"Field","name":{"kind":"Name","value":"version"}},{"kind":"Field","name":{"kind":"Name","value":"url"}}]}}]}}]}}]} as unknown as DocumentNode<GetActualCryptoLegalDocsQuery, GetActualCryptoLegalDocsQueryVariables>;
export const OAuthDocument = {"kind":"Document","definitions":[{"kind":"OperationDefinition","operation":"query","name":{"kind":"Name","value":"OAuth"},"variableDefinitions":[{"kind":"VariableDefinition","variable":{"kind":"Variable","name":{"kind":"Name","value":"input"}},"type":{"kind":"NonNullType","type":{"kind":"NamedType","name":{"kind":"Name","value":"OAuth2Input"}}}}],"selectionSet":{"kind":"SelectionSet","selections":[{"kind":"Field","name":{"kind":"Name","value":"oauth2"},"arguments":[{"kind":"Argument","name":{"kind":"Name","value":"input"},"value":{"kind":"Variable","name":{"kind":"Name","value":"input"}}}],"selectionSet":{"kind":"SelectionSet","selections":[{"kind":"Field","name":{"kind":"Name","value":"url"}},{"kind":"Field","name":{"kind":"Name","value":"statusCode"}},{"kind":"Field","name":{"kind":"Name","value":"__typename"}}]}}]}}]} as unknown as DocumentNode<OAuthQuery, OAuthQueryVariables>;
export const MyCryptoConsentStatusDocument = {"kind":"Document","definitions":[{"kind":"OperationDefinition","operation":"query","name":{"kind":"Name","value":"MyCryptoConsentStatus"},"variableDefinitions":[{"kind":"VariableDefinition","variable":{"kind":"Variable","name":{"kind":"Name","value":"companyId"}},"type":{"kind":"NamedType","name":{"kind":"Name","value":"String"}}}],"selectionSet":{"kind":"SelectionSet","selections":[{"kind":"Field","name":{"kind":"Name","value":"myCryptoConsentStatus"},"arguments":[{"kind":"Argument","name":{"kind":"Name","value":"companyId"},"value":{"kind":"Variable","name":{"kind":"Name","value":"companyId"}}}],"selectionSet":{"kind":"SelectionSet","selections":[{"kind":"Field","name":{"kind":"Name","value":"requiresConsent"}},{"kind":"Field","name":{"kind":"Name","value":"hasAccepted"}},{"kind":"Field","name":{"kind":"Name","value":"currentTermsId"}},{"kind":"Field","name":{"kind":"Name","value":"currentTermsVersion"}}]}}]}}]} as unknown as DocumentNode<MyCryptoConsentStatusQuery, MyCryptoConsentStatusQueryVariables>;
export const MyGetExtraDocument = {"kind":"Document","definitions":[{"kind":"OperationDefinition","operation":"query","name":{"kind":"Name","value":"MyGetExtra"},"variableDefinitions":[{"kind":"VariableDefinition","variable":{"kind":"Variable","name":{"kind":"Name","value":"key"}},"type":{"kind":"NonNullType","type":{"kind":"NamedType","name":{"kind":"Name","value":"String"}}}}],"selectionSet":{"kind":"SelectionSet","selections":[{"kind":"Field","name":{"kind":"Name","value":"myGetExtra"},"arguments":[{"kind":"Argument","name":{"kind":"Name","value":"key"},"value":{"kind":"Variable","name":{"kind":"Name","value":"key"}}}]}]}}]} as unknown as DocumentNode<MyGetExtraQuery, MyGetExtraQueryVariables>;
export const PaybisKybStatusDocument = {"kind":"Document","definitions":[{"kind":"OperationDefinition","operation":"query","name":{"kind":"Name","value":"PaybisKybStatus"},"variableDefinitions":[{"kind":"VariableDefinition","variable":{"kind":"Variable","name":{"kind":"Name","value":"companyId"}},"type":{"kind":"NonNullType","type":{"kind":"NamedType","name":{"kind":"Name","value":"String"}}}}],"selectionSet":{"kind":"SelectionSet","selections":[{"kind":"Field","name":{"kind":"Name","value":"paybisKybStatus"},"arguments":[{"kind":"Argument","name":{"kind":"Name","value":"companyId"},"value":{"kind":"Variable","name":{"kind":"Name","value":"companyId"}}}],"selectionSet":{"kind":"SelectionSet","selections":[{"kind":"Field","name":{"kind":"Name","value":"companyId"}},{"kind":"Field","name":{"kind":"Name","value":"companyName"}},{"kind":"Field","name":{"kind":"Name","value":"createdAt"}},{"kind":"Field","name":{"kind":"Name","value":"email"}},{"kind":"Field","name":{"kind":"Name","value":"id"}},{"kind":"Field","name":{"kind":"Name","value":"notes"}},{"kind":"Field","name":{"kind":"Name","value":"status"}},{"kind":"Field","name":{"kind":"Name","value":"tcStatus"}},{"kind":"Field","name":{"kind":"Name","value":"updatedAt"}},{"kind":"Field","name":{"kind":"Name","value":"updatedBy"}},{"kind":"Field","name":{"kind":"Name","value":"updatedByName"}}]}}]}}]} as unknown as DocumentNode<PaybisKybStatusQuery, PaybisKybStatusQueryVariables>;
export const SendCodeDocument = {"kind":"Document","definitions":[{"kind":"OperationDefinition","operation":"query","name":{"kind":"Name","value":"SendCode"},"variableDefinitions":[{"kind":"VariableDefinition","variable":{"kind":"Variable","name":{"kind":"Name","value":"input"}},"type":{"kind":"NonNullType","type":{"kind":"NamedType","name":{"kind":"Name","value":"SendCodeInput"}}}}],"selectionSet":{"kind":"SelectionSet","selections":[{"kind":"Field","name":{"kind":"Name","value":"sendCode"},"arguments":[{"kind":"Argument","name":{"kind":"Name","value":"input"},"value":{"kind":"Variable","name":{"kind":"Name","value":"input"}}}],"selectionSet":{"kind":"SelectionSet","selections":[{"kind":"Field","name":{"kind":"Name","value":"device"}},{"kind":"Field","name":{"kind":"Name","value":"deviceId"}},{"kind":"Field","name":{"kind":"Name","value":"smsTimeout"}},{"kind":"Field","name":{"kind":"Name","value":"emailTimeout"}},{"kind":"Field","name":{"kind":"Name","value":"smsRetryAfter"}},{"kind":"Field","name":{"kind":"Name","value":"emailRetryAfter"}}]}}]}}]} as unknown as DocumentNode<SendCodeQuery, SendCodeQueryVariables>;
export const SignInDocument = {"kind":"Document","definitions":[{"kind":"OperationDefinition","operation":"query","name":{"kind":"Name","value":"SignIn"},"variableDefinitions":[{"kind":"VariableDefinition","variable":{"kind":"Variable","name":{"kind":"Name","value":"input"}},"type":{"kind":"NonNullType","type":{"kind":"NamedType","name":{"kind":"Name","value":"AuthInput"}}}}],"selectionSet":{"kind":"SelectionSet","selections":[{"kind":"Field","name":{"kind":"Name","value":"signIn"},"arguments":[{"kind":"Argument","name":{"kind":"Name","value":"input"},"value":{"kind":"Variable","name":{"kind":"Name","value":"input"}}}],"selectionSet":{"kind":"SelectionSet","selections":[{"kind":"Field","name":{"kind":"Name","value":"emailRetryAfter"}},{"kind":"Field","name":{"kind":"Name","value":"emailTimeout"}},{"kind":"Field","name":{"kind":"Name","value":"operationId"}},{"kind":"Field","name":{"kind":"Name","value":"phoneNumber"}},{"kind":"Field","name":{"kind":"Name","value":"response"}},{"kind":"Field","name":{"kind":"Name","value":"smsRetryAfter"}},{"kind":"Field","name":{"kind":"Name","value":"smsTimeout"}},{"kind":"Field","name":{"kind":"Name","value":"srpServerProof"}}]}}]}}]} as unknown as DocumentNode<SignInQuery, SignInQueryVariables>;
export const ViewerDocument = {"kind":"Document","definitions":[{"kind":"OperationDefinition","operation":"query","name":{"kind":"Name","value":"Viewer"},"selectionSet":{"kind":"SelectionSet","selections":[{"kind":"Field","name":{"kind":"Name","value":"viewer"},"selectionSet":{"kind":"SelectionSet","selections":[{"kind":"Field","name":{"kind":"Name","value":"EOS"},"selectionSet":{"kind":"SelectionSet","selections":[{"kind":"Field","name":{"kind":"Name","value":"key"}},{"kind":"Field","name":{"kind":"Name","value":"type"}}]}},{"kind":"Field","name":{"kind":"Name","value":"appleUserId"}},{"kind":"Field","name":{"kind":"Name","value":"avatar"},"selectionSet":{"kind":"SelectionSet","selections":[{"kind":"Field","name":{"kind":"Name","value":"url"}},{"kind":"Field","name":{"kind":"Name","value":"id"}}]}},{"kind":"Field","name":{"kind":"Name","value":"birthDate"}},{"kind":"Field","name":{"kind":"Name","value":"blockingStatusInfo"},"selectionSet":{"kind":"SelectionSet","selections":[{"kind":"Field","name":{"kind":"Name","value":"accountIds"}},{"kind":"Field","name":{"kind":"Name","value":"blockName"}},{"kind":"Field","name":{"kind":"Name","value":"isBlocked"}},{"kind":"Field","name":{"kind":"Name","value":"reasons"}},{"kind":"Field","name":{"kind":"Name","value":"types"}}]}},{"kind":"Field","name":{"kind":"Name","value":"chatClientId"}},{"kind":"Field","name":{"kind":"Name","value":"chatClientIdentity"}},{"kind":"Field","name":{"kind":"Name","value":"companies"},"selectionSet":{"kind":"SelectionSet","selections":[{"kind":"Field","name":{"kind":"Name","value":"legalName"}},{"kind":"Field","name":{"kind":"Name","value":"id"}}]}},{"kind":"Field","name":{"kind":"Name","value":"createdAt"}},{"kind":"Field","name":{"kind":"Name","value":"email"},"selectionSet":{"kind":"SelectionSet","selections":[{"kind":"Field","name":{"kind":"Name","value":"confirm"}},{"kind":"Field","name":{"kind":"Name","value":"deviceId"}},{"kind":"Field","name":{"kind":"Name","value":"identity"}},{"kind":"Field","name":{"kind":"Name","value":"type"}}]}},{"kind":"Field","name":{"kind":"Name","value":"externalUserId"}},{"kind":"Field","name":{"kind":"Name","value":"extra"}},{"kind":"Field","name":{"kind":"Name","value":"firstName"}},{"kind":"Field","name":{"kind":"Name","value":"gender"}},{"kind":"Field","name":{"kind":"Name","value":"kybDocumentStatus"}},{"kind":"Field","name":{"kind":"Name","value":"language"}},{"kind":"Field","name":{"kind":"Name","value":"lastLoginDate"}},{"kind":"Field","name":{"kind":"Name","value":"lastName"}},{"kind":"Field","name":{"kind":"Name","value":"leadId"}},{"kind":"Field","name":{"kind":"Name","value":"p256"},"selectionSet":{"kind":"SelectionSet","selections":[{"kind":"Field","name":{"kind":"Name","value":"key"}},{"kind":"Field","name":{"kind":"Name","value":"type"}}]}},{"kind":"Field","name":{"kind":"Name","value":"partnerId"}},{"kind":"Field","name":{"kind":"Name","value":"phone"},"selectionSet":{"kind":"SelectionSet","selections":[{"kind":"Field","name":{"kind":"Name","value":"confirm"}},{"kind":"Field","name":{"kind":"Name","value":"deviceId"}},{"kind":"Field","name":{"kind":"Name","value":"identity"}},{"kind":"Field","name":{"kind":"Name","value":"type"}}]}},{"kind":"Field","name":{"kind":"Name","value":"referrerInfo"},"selectionSet":{"kind":"SelectionSet","selections":[{"kind":"Field","name":{"kind":"Name","value":"id"}},{"kind":"Field","name":{"kind":"Name","value":"inviteCode"}},{"kind":"Field","name":{"kind":"Name","value":"referrals"}}]}},{"kind":"Field","name":{"kind":"Name","value":"role"}},{"kind":"Field","name":{"kind":"Name","value":"service"}},{"kind":"Field","name":{"kind":"Name","value":"status"}},{"kind":"Field","name":{"kind":"Name","value":"sub"}},{"kind":"Field","name":{"kind":"Name","value":"tariff"}},{"kind":"Field","name":{"kind":"Name","value":"timezone"}},{"kind":"Field","name":{"kind":"Name","value":"updatedAt"}},{"kind":"Field","name":{"kind":"Name","value":"userId"}},{"kind":"Field","name":{"kind":"Name","value":"username"}}]}}]}}]} as unknown as DocumentNode<ViewerQuery, ViewerQueryVariables>;